<template>
  <div>
    <v-row>
      <v-col cols="12" sm="12">
        <h2 class="text--primary text-xl font-weight-medium">
          {{ $t("Omat tiedot ja asetukset") }}
        </h2>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="8" sm="12">
        <v-card>
          <v-list>
            <template>
              <v-list-item>
                <v-list-item-avatar style="cursor: pointer" @click="editUserImage">
                  <v-img v-if="profileImage" :src="profileImage"></v-img>
                  <v-icon v-else>{{ icons.mdiAccountCircle }}</v-icon>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title>
                    {{ profile.first_name }} {{ profile.last_name }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-avatar>
                  <v-icon size="24px">
                    {{ icons.mdiAt }}
                  </v-icon>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title>
                    {{ profile.email ? profile.email : "-" }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-avatar>
                  <v-icon size="24px">
                    {{ icons.mdiPhone ? icons.mdiPhone : "-" }}
                  </v-icon>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title>
                    {{ profile.phone }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-avatar>
                  <v-icon size="24px">
                    {{ icons.mdiEarth }}
                  </v-icon>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title>
                    {{ displayTimezone }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ $t("Aikavyöhyke") }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-select outlined v-model="profile.status" :label="$t('Oma tila')" :items="statusValues"
                    item-value="id" item-text="title" @change="changeStatus">
                    <template v-slot:prepend-inner>
                      <v-icon class="mr-1" :color="profile.status == 1 ? 'success' : 'error'">
                        {{ icons.mdiCircle }}</v-icon>
                    </template>
                  </v-select>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>
      <v-col cols="12" md="4" sm="12">
        <v-card>
          <v-card-title>{{ $t("Tietoa Piiri-sovelluksesta") }}</v-card-title>
          <v-list>
            <template>
              <v-list-item target="_blank" href="https://www.piiri.io">
                <v-list-item-avatar>
                  <v-icon size="24px">
                    {{ icons.mdiInformationOutline }}</v-icon>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title>
                    {{ $t("Tietoa Piiri-sovelluksesta") }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item target="_blank" href="https://www.piiri.io/tietosuojaseloste">
                <v-list-item-avatar>
                  <v-icon size="24px">
                    {{ icons.mdiFlag }}
                  </v-icon>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title>
                    {{ $t("Tietosuojaseloste") }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
        <br />
      </v-col>
    </v-row>
    <v-dialog v-model="dialog" max-width="440px">
      <v-card>
        <my-profile-image-form :current-image="profileImage" :width="400" :height="400"
          @updated="pictureUpdated"></my-profile-image-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "@vue/composition-api";
import useAuth from "@/useAuth";
import api from "@/api";
import { useTranslations } from "@/@core/libs/i18n";
import myProfileImageForm from "./components/my_profile_image_form";

import {
  mdiCircle,
  mdiFlag,
  mdiAccountCircle,
  mdiAccountOutline,
  mdiInformationOutline,
  mdiAt,
  mdiPhone,
  mdiEarth,
} from "@mdi/js";

export default {
  components: {
    myProfileImageForm,
  },
  setup() {
    const profile = ref({});
    const { user } = useAuth();
    const { t } = useTranslations();
    const dialog = ref(false);
    const statusValues = [
      {
        title: t("Töissä"),
        id: 1,
      },
      {
        title: t("Poissa töistä"),
        id: 2,
      },
    ];

    const fetchProfile = async () => {
      const response = await api.getUserProfile();
      if (response.ok) {
        profile.value = response.data;
      }
    };

    const setStatus = async (newStatus) => {
      const response = await api.setUserStatus(newStatus);
      if (!response.ok) {
        profile.value.status = newStatus === 2 ? 1 : 2;
      }
    };

    const profileImage = computed(() => {
      if (profile.value.picture) {
        if (
          profile.value.picture &&
          Object.keys(profile.value.picture).length
        ) {
          return profile.value.picture.thumbnail;
        }
      }
      return null;
    });

    const displayTimezone = computed(() => {
      return user.value.timezone || "Europe/Helsinki";
    });

    const editUserImage = () => {
      dialog.value = true;
    };

    const pictureUpdated = (data) => {
      dialog.value = false;
      fetchProfile();
    };

    const changeStatus = async (s) => {
      setStatus(s);
    };

    onMounted(() => {
      fetchProfile();
    });

    return {
      profile,
      statusValues,
      setStatus,
      profileImage,
      changeStatus,
      editUserImage,
      pictureUpdated,
      dialog,
      t,
      displayTimezone,
      icons: {
        mdiFlag,
        mdiAccountCircle,
        mdiInformationOutline,
        mdiCircle,
        mdiAccountOutline,
        mdiAt,
        mdiPhone,
        mdiEarth,
      },
    };
  },
};
</script>

<style></style>
